/**
 * TagFormModal — create and edit tags.
 *
 * POST /tags  (create): { name, category, color }
 * PATCH /tags/:id (edit): { name, category, color }
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

// ── Constants ──────────────────────────────────────────────────────────────────

export const TAG_CATEGORIES = ['Allmän', 'Deltagare', 'Verksamheter', 'Prioritet'];

export const TAG_COLORS = [
  { value: '#EF4444', label: 'Röd'    },
  { value: '#F97316', label: 'Orange' },
  { value: '#EAB308', label: 'Gul'    },
  { value: '#22C55E', label: 'Grön'   },
  { value: '#3B82F6', label: 'Blå'    },
  { value: '#8B5CF6', label: 'Lila'   },
  { value: '#EC4899', label: 'Rosa'   },
];

const EMPTY = { name: '', category: '', color: '' };

// ── Component ──────────────────────────────────────────────────────────────────

const TagFormModal = ({
  open,
  onClose,
  onSaved,
  editTag = null,       // null → create mode; tag object → edit mode
  existingNames = [],   // [string] — used for duplicate check
}) => {
  const [formData, setFormData]   = useState(EMPTY);
  const [errors, setErrors]       = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const modalRef    = useRef(null);
  const nameInputRef = useRef(null);

  const isEdit = !!editTag;

  // ── Seed form on open ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    setFormData(
      editTag
        ? { name: editTag.name, category: editTag.category, color: editTag.color }
        : EMPTY
    );
    setErrors({});
    setSubmitted(false);
  }, [open, editTag]);

  // ── Body lock + initial focus ────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => nameInputRef.current?.focus(), 40);
    return () => { clearTimeout(t); document.body.style.overflow = ''; };
  }, [open]);

  // ── Escape ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // ── Focus trap ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const el = modalRef.current;
    if (!el) return;
    const trap = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        el.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    el.addEventListener('keydown', trap);
    return () => el.removeEventListener('keydown', trap);
  }, [open]);

  // ── Validation ───────────────────────────────────────────────────────────────

  const validate = (data) => {
    const errs = {};
    const trimmedName = data.name.trim();
    if (!trimmedName)               errs.name = 'Namn är obligatoriskt';
    else if (trimmedName.length > 50) errs.name = 'Max 50 tecken';
    else {
      const lowerName = trimmedName.toLowerCase();
      const isDuplicate = existingNames.some(n =>
        n.toLowerCase() === lowerName &&
        (!isEdit || n.toLowerCase() !== editTag.name.toLowerCase())
      );
      if (isDuplicate) errs.name = 'En tagg med det namnet finns redan';
    }
    if (!data.category) errs.category = 'Kategori är obligatorisk';
    if (!data.color)    errs.color    = 'Färg är obligatorisk';
    return errs;
  };

  const hasErrors = (errs) => Object.values(errs).some(Boolean);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const setField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (submitted) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate(formData);
    setErrors(errs);
    if (hasErrors(errs)) return;

    setSubmitting(true);
    try {
      const payload = {
        name:     formData.name.trim(),
        category: formData.category,
        color:    formData.color,
      };
      if (isEdit) {
        await api.patch(`/tags/${editTag.id}`, payload);
        toast.success('Tagg uppdaterad!');
      } else {
        await api.post('/tags', payload);
        toast.success('Tagg skapad!');
      }
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte spara taggen');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (!open) return null;

  const selectClass = (err) =>
    `w-full h-10 px-3 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
      err ? 'border-destructive focus:ring-destructive' : 'border-input'
    }`;

  // Live preview
  const previewName  = formData.name.trim() || 'Förhandsvisning';
  const previewColor = formData.color || '#e5e7eb';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.25)' }}
      data-testid="tag-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tag-modal-title"
        data-testid="tag-modal"
        className="relative bg-white w-full max-w-md rounded-xl shadow-2xl flex flex-col animate-in fade-in-0 zoom-in-95 duration-150"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0 rounded-t-xl">
          <h2
            id="tag-modal-title"
            className="text-xl font-heading font-bold"
            style={{ color: '#333333' }}
          >
            {isEdit ? 'Redigera tagg' : 'Ny tagg'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Stäng modal"
            className="rounded-md p-1.5 opacity-60 hover:opacity-100 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-[#F9CF3F] focus:ring-offset-2 transition-opacity"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          <form
            id="tag-form"
            onSubmit={handleSubmit}
            noValidate
            className="px-6 py-6 space-y-5"
            data-testid="tag-form"
          >

            {/* Live preview */}
            <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                Förhandsvisning
              </span>
              <span
                data-testid="tag-preview"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold text-white"
                style={{ background: previewColor }}
                aria-label={`Förhandsvisning: ${previewName}`}
              >
                <span className="w-2 h-2 rounded-full bg-white/60 shrink-0" />
                {previewName}
              </span>
            </div>

            {/* Namn */}
            <div className="space-y-1.5">
              <Label htmlFor="tag-name">
                Namn
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </Label>
              <Input
                id="tag-name"
                ref={nameInputRef}
                data-testid="tag-name-input"
                placeholder="Taggnamn"
                maxLength={50}
                value={formData.name}
                onChange={e => setField('name', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'tag-name-err' : undefined}
                className={errors.name ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {errors.name && (
                <p id="tag-name-err" role="alert" className="text-xs text-destructive">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Kategori */}
            <div className="space-y-1.5">
              <Label htmlFor="tag-category">
                Kategori
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </Label>
              <select
                id="tag-category"
                data-testid="tag-category-select"
                value={formData.category}
                onChange={e => setField('category', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.category}
                aria-describedby={errors.category ? 'tag-category-err' : undefined}
                className={selectClass(errors.category)}
              >
                <option value="">Välj kategori...</option>
                {TAG_CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {errors.category && (
                <p id="tag-category-err" role="alert" className="text-xs text-destructive">
                  {errors.category}
                </p>
              )}
            </div>

            {/* Färg */}
            <div className="space-y-2">
              <Label>
                Färg
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </Label>
              <div
                role="radiogroup"
                aria-label="Välj färg"
                aria-required="true"
                className="flex flex-wrap gap-3"
                data-testid="tag-color-picker"
              >
                {TAG_COLORS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`color-option-${value}`}
                    role="radio"
                    aria-checked={formData.color === value}
                    aria-label={label}
                    onClick={() => setField('color', value)}
                    className="w-8 h-8 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-transform hover:scale-110"
                    style={{
                      background:  value,
                      boxShadow:   formData.color === value
                        ? `0 0 0 3px white, 0 0 0 5px ${value}`
                        : undefined,
                      transform:   formData.color === value ? 'scale(1.15)' : undefined,
                    }}
                    title={label}
                  />
                ))}
              </div>
              {errors.color && (
                <p role="alert" className="text-xs text-destructive" data-testid="tag-color-error">
                  {errors.color}
                </p>
              )}
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-white shrink-0 rounded-b-xl">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            className="rounded-full"
          >
            Avbryt
          </Button>
          <Button
            type="submit"
            form="tag-form"
            data-testid="tag-submit-btn"
            disabled={submitting}
            className="rounded-full font-bold px-8"
            style={{ background: '#F9CF3F', color: '#333333' }}
          >
            {submitting ? 'Sparar...' : isEdit ? 'Uppdatera tagg' : 'Spara tagg'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TagFormModal;
